/**
 * Ciò che resta del ragionamento sul rilascio nel browser: nulla che giudichi.
 *
 * Il verdetto lo formula il server, in `domain/deploy/requisiti.ts`, ed è lo
 * stesso su cui l'avvio decide di rifiutare. Qui si incrociano soltanto i
 * servizi del compose con gli instradamenti già configurati.
 */

/** Un servizio dello stack visto dal punto di vista dell'instradamento. */
export interface ServizioInstradabile {
  readonly servizio: string;
  /** Porta interna dedotta dal compose: `null` quando il compose non la dichiara. */
  readonly portaProposta: number | null;
  /** Il compose ne dichiara più d'una: la scelta va confermata. */
  readonly piuPorte: boolean;
  readonly instradato: { readonly domain: string; readonly containerPort: number } | null;
  /** Falso per un instradamento rimasto verso un servizio che non esiste più. */
  readonly nelCompose: boolean;
}

/**
 * Incrocia i servizi del compose con gli instradamenti già configurati.
 *
 * I servizi fissi restano fuori: non hanno generazioni su cui spostare il
 * traffico. Gli orfani (instradamento verso un servizio scomparso o diventato
 * fisso) restano, così si possono rimuovere.
 */
export function serviziInstradabili(
  servizi: readonly { name: string; portaProposta: number | null; piuPorte: boolean; fisso?: boolean }[],
  instradamenti: readonly { service: string; domain: string; containerPort: number }[],
): readonly ServizioInstradabile[] {
  const perServizio = new Map(instradamenti.map((route) => [route.service, route]));
  const nonFissi = servizi.filter((servizio) => !servizio.fisso);

  const dalCompose = nonFissi.map((servizio): ServizioInstradabile => {
    const route = perServizio.get(servizio.name);

    return {
      servizio: servizio.name,
      portaProposta: servizio.portaProposta,
      piuPorte: servizio.piuPorte,
      instradato: route ? { domain: route.domain, containerPort: route.containerPort } : null,
      nelCompose: true,
    };
  });

  // Un instradamento verso un servizio rinominato, rimosso o diventato fisso
  // punta nel vuoto: toglierlo dall'elenco lo renderebbe invisibile proprio a
  // chi deve rimuoverlo.
  const nelCompose = new Set(nonFissi.map((s) => s.name));
  const orfani = instradamenti
    .filter((route) => !nelCompose.has(route.service))
    .map(
      (route): ServizioInstradabile => ({
        servizio: route.service,
        portaProposta: null,
        piuPorte: false,
        instradato: { domain: route.domain, containerPort: route.containerPort },
        nelCompose: false,
      }),
    );

  // Prima ciò che è già deciso, poi ciò su cui si decide, poi il resto: con
  // dodici servizi i candidati sparsi in mezzo a database e code si perdono.
  const peso = (s: ServizioInstradabile) => (s.instradato ? 0 : s.portaProposta !== null ? 1 : 2);

  return [...[...dalCompose].sort((a, b) => peso(a) - peso(b)), ...orfani];
}
